import React, { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { FaEye, FaEyeSlash, FaLock } from "react-icons/fa";
import { supabase } from "../supabaseClient";
import { MAINTENANCE_OWNER_EMAIL, normaliseEmail } from "../utils/maintenance";
import Footer from "../components/Footer";
import nhsLogo from "../assets/nhs-logo.png";

const REQUIRED_REFERRAL_CODE = process.env.REACT_APP_REFERRAL_CODE;
const LAST_ROUTE_KEY = "pguh:lastRoute";

function getReturnPath(location) {
  const requestedPath = location.state?.from;
  if (
    typeof requestedPath === "string"
    && requestedPath.startsWith("/")
    && !requestedPath.startsWith("//")
  ) {
    return requestedPath;
  }

  const storedPath = localStorage.getItem(LAST_ROUTE_KEY);
  if (storedPath?.startsWith("/intranet/")) return storedPath;

  return "/intranet/portal";
}

export default function Login({ maintenanceAdminOnly = false }) {
  const navigate = useNavigate();
  const location = useLocation();

  const [mode, setMode] = useState("login");
  const [loginIdentifier, setLoginIdentifier] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);

  const [signupWithEmail, setSignupWithEmail] = useState(true);
  const [email, setEmail] = useState("");
  const [fullName, setFullName] = useState("");
  const [robloxName, setRobloxName] = useState("");
  const [discordName, setDiscordName] = useState("");
  const [referralCode, setReferralCode] = useState("");

  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState(() =>
    new URLSearchParams(location.search).get("suspended") === "1"
      ? "Your PGUH account has been suspended by Management. Contact Management for further information."
      : "",
  );
  const [successMsg, setSuccessMsg] = useState("");

  const resetMessages = () => {
    setErrorMsg("");
    setSuccessMsg("");
  };

  const switchMode = (nextMode) => {
    resetMessages();
    setMode(nextMode);
  };

  const resolveEmail = async (identifier) => {
    const value = identifier.trim();
    if (value.includes("@")) return value;

    const { data, error } = await supabase
      .from("profiles")
      .select("email")
      .ilike("roblox_name", value)
      .maybeSingle();

    if (error || !data?.email) {
      throw new Error("We could not find an account with those details.");
    }

    return data.email;
  };

  const handleLogin = async (event) => {
    event.preventDefault();
    resetMessages();
    setLoading(true);

    try {
      const loginEmail = await resolveEmail(loginIdentifier);

      const { data: signInData, error } = await supabase.auth.signInWithPassword({
        email: loginEmail,
        password,
      });

      if (error) throw error;

      const signedInUserId = signInData?.user?.id;
      if (signedInUserId) {
        let { data: accountProfile } = await supabase
          .from("profiles")
          .select("position, suspended, suspension_reason, suspended_until")
          .eq("id", signedInUserId)
          .maybeSingle();

        if (accountProfile?.suspended && accountProfile?.suspended_until) {
          const endTime = new Date(accountProfile.suspended_until).getTime();
          if (Number.isFinite(endTime) && endTime <= Date.now()) {
            await supabase.rpc("ruht_clear_my_expired_suspension");
            const refreshed = await supabase
              .from("profiles")
              .select("position, suspended, suspension_reason, suspended_until")
              .eq("id", signedInUserId)
              .maybeSingle();
            accountProfile = refreshed.data || accountProfile;
          }
        }

        if (accountProfile?.suspended) {
          await supabase.auth.signOut({ scope: "local" });
          const untilText = accountProfile.suspended_until
            ? ` This suspension is scheduled to end ${new Intl.DateTimeFormat("en-GB", { dateStyle: "medium", timeStyle: "short" }).format(new Date(accountProfile.suspended_until))}.`
            : " This suspension is indefinite and must be lifted by management.";
          throw new Error(`Your PGUH account is suspended. ${accountProfile.suspension_reason || "Contact management for further information."}${untilText}`);
        }

        if (maintenanceAdminOnly) {
          const isOwner = normaliseEmail(loginEmail) === MAINTENANCE_OWNER_EMAIL;
          const isManagement = accountProfile?.position === "Management";
          if (!isOwner && !isManagement) {
            await supabase.auth.signOut({ scope: "local" });
            throw new Error("Maintenance mode is active. Only Management accounts can sign in until maintenance is disabled.");
          }
        }
      }

      navigate(getReturnPath(location), { replace: true });
    } catch (error) {
      setErrorMsg(error.message || "We could not sign you in.");
      setLoading(false);
    }
  };

  const handleSendReset = async (event) => {
    event.preventDefault();
    resetMessages();
    setLoading(true);

    try {
      if (!loginIdentifier.trim()) {
        throw new Error("Enter your email address or Roblox username.");
      }

      const resetEmail = await resolveEmail(loginIdentifier);

      if (resetEmail.toLowerCase().endsWith("@ruht.cloud")) {
        throw new Error(
          "Roblox-only accounts do not have an email inbox. Contact PGUH management if you need your password reset.",
        );
      }

      const { error } = await supabase.auth.resetPasswordForEmail(resetEmail, {
        redirectTo: `${window.location.origin}/reset-password`,
      });

      if (error) throw error;
      setSuccessMsg(
        "A password reset link has been sent. Check your inbox and junk folder.",
      );
    } catch (error) {
      setErrorMsg(error.message || "We could not send the reset link.");
    } finally {
      setLoading(false);
    }
  };

  const handleSignup = async (event) => {
    event.preventDefault();
    resetMessages();
    setLoading(true);

    try {
      if (!referralCode) throw new Error("Enter your referral code.");
      if (referralCode !== REQUIRED_REFERRAL_CODE) {
        throw new Error("The referral code is not valid.");
      }
      if (!fullName.trim()) throw new Error("Enter your full name.");
      if (!signupWithEmail && !robloxName.trim()) {
        throw new Error("Enter your Roblox username.");
      }
      if (signupWithEmail && !email.trim()) {
        throw new Error("Enter your email address.");
      }
      if (password.length < 6) {
        throw new Error("Your password must contain at least 6 characters.");
      }

      if (!signupWithEmail) {
        const response = await fetch("/api/signupRoblox", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            fullName: fullName.trim(),
            robloxName: robloxName.trim(),
            discordName: discordName.trim(),
            password,
            referralCode,
          }),
        });

        const result = await response.json().catch(() => ({}));

        if (!response.ok) {
          throw new Error(
            result.error || "The Roblox account could not be created.",
          );
        }

        const { error: signInError } = await supabase.auth.signInWithPassword({
          email: result.email,
          password,
        });

        if (signInError) {
          setMode("login");
          setLoginIdentifier(robloxName.trim());
          throw new Error(
            "Your Roblox account was created, but automatic sign-in failed. Sign in using your Roblox username and the password you just created.",
          );
        }

        navigate("/intranet/portal", { replace: true });
        return;
      }

      if (robloxName.trim()) {
        const { data: existing } = await supabase
          .from("profiles")
          .select("id")
          .ilike("roblox_name", robloxName.trim())
          .maybeSingle();

        if (existing) {
          throw new Error("That Roblox username is already registered.");
        }
      }

      const finalEmail = email.trim();

      const { data, error } = await supabase.auth.signUp({
        email: finalEmail,
        password,
      });

      if (error) throw error;
      if (!data.user) throw new Error("The account could not be created.");

      const { error: profileError } = await supabase.from("profiles").upsert(
        {
          id: data.user.id,
          email: finalEmail,
          full_name: fullName.trim(),
          position: "Visitor",
          roblox_name: robloxName.trim() || null,
          discord_name: discordName.trim() || null,
        },
        { onConflict: "id" },
      );

      if (profileError) {
        throw new Error(
          "Your account was created, but your staff profile could not be saved.",
        );
      }

      if (data.session) {
        navigate("/intranet/portal", { replace: true });
        return;
      }

      setSuccessMsg(
        "Your account has been created. Check your email to confirm it, then sign in.",
      );
      setMode("login");
      setLoginIdentifier(finalEmail);
    } catch (error) {
      setErrorMsg(error.message || "We could not create your account.");
    } finally {
      setLoading(false);
    }
  };

  const title = maintenanceAdminOnly
    ? "Management maintenance sign in"
    : mode === "forgot"
      ? "Reset your password"
      : mode === "signup"
        ? "Create a PGUH account"
        : "Sign in to staff services";

  return (
    <div className="pguh-auth-page">
      <header className="pguh-public-header">
        <div className="nhsuk-width-container pguh-public-header__inner">
          <img src={nhsLogo} alt="NHS" />
          <div>
            <strong>Royal University Hospitals Trust</strong>
            <span>PGUH Services</span>
          </div>
        </div>
      </header>

      <main id="main-content" tabIndex="-1" className="pguh-auth-main">
        <div className="nhsuk-width-container pguh-auth-grid">
          <section className="pguh-auth-intro" aria-labelledby="service-title">
            <span className="pguh-eyebrow">{maintenanceAdminOnly ? "Maintenance access" : "Secure staff access"}</span>
            <h1 id="service-title" className="nhsuk-heading-xl">
              {maintenanceAdminOnly ? "Management maintenance access" : "One place for your hospital services"}
            </h1>
            <p className="nhsuk-body-l">
              {maintenanceAdminOnly
                ? `Maintenance mode is active. Management accounts can sign in and continue using the site.`
                : "Access eCAMIS, schedules, appointments, wards, reports and management tools using your existing PGUH account."}
            </p>

            <div className="nhsuk-inset-text pguh-security-note">
              <FaLock aria-hidden="true" />
              <p>
                This service is restricted to authorised users. Do not share
                patient or staff information outside approved systems.
              </p>
            </div>
          </section>

          <section className="pguh-auth-card" aria-labelledby="auth-title">
            <h2 id="auth-title" className="nhsuk-heading-l">
              {title}
            </h2>

            {!maintenanceAdminOnly && mode !== "forgot" && (
              <div className="pguh-auth-tabs" aria-label="Account options">
                <button
                  type="button"
                  className={mode === "login" ? "is-active" : ""}
                  onClick={() => switchMode("login")}
                  aria-pressed={mode === "login"}
                >
                  Sign in
                </button>
                <button
                  type="button"
                  className={mode === "signup" ? "is-active" : ""}
                  onClick={() => switchMode("signup")}
                  aria-pressed={mode === "signup"}
                >
                  Create account
                </button>
              </div>
            )}

            {errorMsg && (
              <div
                className="nhsuk-error-summary"
                data-module="nhsuk-error-summary"
                role="alert"
                aria-labelledby="auth-error-title"
              >
                <h3 className="nhsuk-error-summary__title" id="auth-error-title">
                  There is a problem
                </h3>
                <div className="nhsuk-error-summary__body">
                  <p className="nhsuk-u-margin-bottom-0">{errorMsg}</p>
                </div>
              </div>
            )}

            {successMsg && (
              <div className="nhsuk-inset-text pguh-success-message" role="status">
                <p className="nhsuk-u-margin-bottom-0">{successMsg}</p>
              </div>
            )}

            {!maintenanceAdminOnly && mode === "forgot" ? (
              <form onSubmit={handleSendReset} noValidate>
                <div className="nhsuk-form-group">
                  <label className="nhsuk-label" htmlFor="reset-identifier">
                    Email address or Roblox username
                  </label>
                  <div className="nhsuk-hint" id="reset-identifier-hint">
                    Email accounts receive a reset link. Roblox-only accounts use an internal @ruht.cloud address and cannot receive reset emails.
                  </div>
                  <input
                    id="reset-identifier"
                    className="nhsuk-input"
                    value={loginIdentifier}
                    onChange={(event) => setLoginIdentifier(event.target.value)}
                    aria-describedby="reset-identifier-hint"
                    autoComplete="username"
                    required
                  />
                </div>

                <button
                  type="submit"
                  className="nhsuk-button pguh-full-button"
                  disabled={loading}
                >
                  {loading ? "Sending reset link…" : "Send reset link"}
                </button>

                <button
                  type="button"
                  className="nhsuk-button nhsuk-button--secondary pguh-full-button"
                  onClick={() => switchMode("login")}
                >
                  Back to sign in
                </button>
              </form>
            ) : (
              <form
                onSubmit={!maintenanceAdminOnly && mode === "signup" ? handleSignup : handleLogin}
                noValidate
              >
                {(maintenanceAdminOnly || mode === "login") && (
                  <div className="nhsuk-form-group">
                    <label className="nhsuk-label" htmlFor="login-identifier">
                      {maintenanceAdminOnly ? "Management email or Roblox username" : "Email address or Roblox username"}
                    </label>
                    <input
                      id="login-identifier"
                      className="nhsuk-input"
                      type="text"
                      value={loginIdentifier}
                      onChange={(event) => setLoginIdentifier(event.target.value)}
                      autoComplete="username"
                      required
                    />
                  </div>
                )}

                {!maintenanceAdminOnly && mode === "signup" && (
                  <>
                    <fieldset className="nhsuk-fieldset nhsuk-u-margin-bottom-4">
                      <legend className="nhsuk-fieldset__legend nhsuk-fieldset__legend--s">
                        How would you like to register?
                      </legend>
                      <div className="nhsuk-radios nhsuk-radios--inline">
                        <div className="nhsuk-radios__item">
                          <input
                            className="nhsuk-radios__input"
                            id="register-email"
                            name="registration-method"
                            type="radio"
                            checked={signupWithEmail}
                            onChange={() => setSignupWithEmail(true)}
                          />
                          <label
                            className="nhsuk-label nhsuk-radios__label"
                            htmlFor="register-email"
                          >
                            Email
                          </label>
                        </div>
                        <div className="nhsuk-radios__item">
                          <input
                            className="nhsuk-radios__input"
                            id="register-roblox"
                            name="registration-method"
                            type="radio"
                            checked={!signupWithEmail}
                            onChange={() => setSignupWithEmail(false)}
                          />
                          <label
                            className="nhsuk-label nhsuk-radios__label"
                            htmlFor="register-roblox"
                          >
                            Roblox account
                          </label>
                        </div>
                      </div>
                    </fieldset>

                    {signupWithEmail && (
                      <div className="nhsuk-form-group">
                        <label className="nhsuk-label" htmlFor="signup-email">
                          Email address
                        </label>
                        <input
                          id="signup-email"
                          className="nhsuk-input"
                          type="email"
                          value={email}
                          onChange={(event) => setEmail(event.target.value)}
                          autoComplete="email"
                          required
                        />
                      </div>
                    )}

                    <div className="nhsuk-form-group">
                      <label className="nhsuk-label" htmlFor="full-name">
                        Full name
                      </label>
                      <input
                        id="full-name"
                        className="nhsuk-input"
                        value={fullName}
                        onChange={(event) => setFullName(event.target.value)}
                        autoComplete="name"
                        required
                      />
                    </div>

                    <div className="nhsuk-form-group">
                      <label className="nhsuk-label" htmlFor="roblox-name">
                        Roblox username
                      </label>
                      <div className="nhsuk-hint" id="roblox-name-hint">
                        For Roblox registration, we verify the username and create an internal sign-in address such as username.userid@ruht.cloud. No email confirmation is required.
                      </div>
                      <input
                        id="roblox-name"
                        className="nhsuk-input"
                        value={robloxName}
                        onChange={(event) => setRobloxName(event.target.value)}
                        aria-describedby="roblox-name-hint"
                        required={!signupWithEmail}
                      />
                    </div>

                    <div className="nhsuk-form-group">
                      <label className="nhsuk-label" htmlFor="discord-name">
                        Discord username <span className="nhsuk-hint">(optional)</span>
                      </label>
                      <input
                        id="discord-name"
                        className="nhsuk-input"
                        value={discordName}
                        onChange={(event) => setDiscordName(event.target.value)}
                      />
                    </div>

                    <div className="nhsuk-form-group">
                      <label className="nhsuk-label" htmlFor="referral-code">
                        Referral code
                      </label>
                      <input
                        id="referral-code"
                        className="nhsuk-input nhsuk-input--width-20"
                        value={referralCode}
                        onChange={(event) => setReferralCode(event.target.value)}
                        autoComplete="off"
                        required
                      />
                    </div>
                  </>
                )}

                <div className="nhsuk-form-group">
                  <label className="nhsuk-label" htmlFor="auth-password">
                    Password
                  </label>
                  {!maintenanceAdminOnly && mode === "signup" && (
                    <div className="nhsuk-hint" id="password-hint">
                      Use at least 6 characters.
                    </div>
                  )}
                  <div className="pguh-password-field">
                    <input
                      id="auth-password"
                      className="nhsuk-input"
                      type={showPassword ? "text" : "password"}
                      value={password}
                      onChange={(event) => setPassword(event.target.value)}
                      aria-describedby={!maintenanceAdminOnly && mode === "signup" ? "password-hint" : undefined}
                      autoComplete={
                        !maintenanceAdminOnly && mode === "signup" ? "new-password" : "current-password"
                      }
                      required
                    />
                    <button
                      type="button"
                      className="pguh-password-toggle"
                      onClick={() => setShowPassword((visible) => !visible)}
                      aria-label={showPassword ? "Hide password" : "Show password"}
                    >
                      {showPassword ? <FaEyeSlash /> : <FaEye />}
                    </button>
                  </div>
                </div>

                <button
                  type="submit"
                  className="nhsuk-button pguh-full-button"
                  disabled={loading}
                >
                  {loading
                    ? !maintenanceAdminOnly && mode === "signup"
                      ? "Creating account…"
                      : "Signing in…"
                    : !maintenanceAdminOnly && mode === "signup"
                      ? "Create account"
                      : maintenanceAdminOnly
                        ? "Sign in as Management"
                        : "Sign in"}
                </button>

                {!maintenanceAdminOnly && mode === "login" && (
                  <button
                    type="button"
                    className="pguh-text-button"
                    onClick={() => switchMode("forgot")}
                  >
                    Forgotten your password?
                  </button>
                )}
              </form>
            )}

            <p className="nhsuk-body-s pguh-auth-policy">
              By using this service, you agree to the <Link to="/terms">terms</Link>{" "}
              and <Link to="/privacy">privacy policy</Link>.
            </p>
          </section>
        </div>
      </main>

      <Footer />
    </div>
  );
}
